import type { FC } from 'react';
import { useEffect, useMemo } from 'react';

export interface ShortcutBinding {
  combo: string;
  handler: (event: KeyboardEvent) => void;
  preventDefault?: boolean;
  stopPropagation?: boolean;
  allowInInputs?: boolean;
  enabled?: boolean;
}

interface ParsedCombo {
  key: string;
  ctrl: boolean;
  alt: boolean;
  shift: boolean;
}

interface NormalizedBinding {
  key: string;
  ctrl: boolean;
  alt: boolean;
  shift: boolean;
  binding: ShortcutBinding;
}

export interface ShortcutListenerProps {
  bindings: ShortcutBinding[];
  disabled?: boolean;
}

function normalizeKey(key: string | undefined): string {
  if (!key) return '';
  const lower = key.toLowerCase();
  if (lower.startsWith('f') && lower.length <= 3) return lower;
  return lower;
}

function parseCombo(combo: string): ParsedCombo {
  const parts = combo.split('+').map(p => p.trim());
  let ctrl = false;
  let alt = false;
  let shift = false;
  let key = '';

  parts.forEach(part => {
    const p = normalizeKey(part);
    if (p === 'ctrl' || p === 'control') ctrl = true;
    else if (p === 'alt') alt = true;
    else if (p === 'shift') shift = true;
    else key = p;
  });

  return { key, ctrl, alt, shift };
}

const ShortcutListener: FC<ShortcutListenerProps> = ({ bindings, disabled }) => {
  const normalized = useMemo<NormalizedBinding[]>(() => {
    return bindings
      .filter(b => b.enabled ?? true)
      .map(b => {
        const parsed = parseCombo(b.combo);
        return { ...parsed, binding: b };
      });
  }, [bindings]);

  useEffect(() => {
    if (disabled) return;

    const handleKeyDown = (event: KeyboardEvent): void => {
      const activeEl = document.activeElement as HTMLElement | null;
      const isInputFocused =
        !!activeEl &&
        (activeEl.tagName === 'INPUT' ||
          activeEl.tagName === 'TEXTAREA' ||
          activeEl.isContentEditable);

      if (!event.key) return;

      const pressedKey = normalizeKey(event.key);

      for (const nb of normalized) {
        const { binding } = nb;
        const keyMatch = pressedKey === nb.key;
        const ctrlMatch = event.ctrlKey === nb.ctrl;
        const altMatch = event.altKey === nb.alt;
        const shiftMatch = event.shiftKey === nb.shift;
        const noModifiers = !nb.ctrl && !nb.alt && !nb.shift;
        const noModifiersPressed =
          !event.ctrlKey && !event.shiftKey && !event.altKey && !event.metaKey;

        const match =
          keyMatch &&
          ctrlMatch &&
          altMatch &&
          shiftMatch &&
          (noModifiers ? noModifiersPressed : true);

        if (match) {
          if (binding.preventDefault ?? true) event.preventDefault();
          if (binding.stopPropagation ?? false) event.stopPropagation();
          if (!isInputFocused || (binding.allowInInputs ?? false)) {
            binding.handler(event);
            break;
          }
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [normalized, disabled]);

  return null;
};

export default ShortcutListener;
